import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2 } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api from '../../lib/api';

const TYPES = ['asset', 'liability', 'equity', 'revenue', 'expense'];
const TYPE_LABELS = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity', revenue: 'Revenue', expense: 'Expenses' };
const TYPE_COLORS = { asset: '#1E40AF', liability: '#EF4444', equity: '#1E40AF', revenue: '#1E40AF', expense: '#92400E' };

const ChartOfAccounts = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editAcct, setEditAcct] = useState(null);
  const [form, setForm] = useState({ account_code: '', account_name: '', account_type: 'expense', account_category: 'other' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchAccounts();
  }, [navigate]);

  const fetchAccounts = async () => {
    try { setLoading(true); const r = await api.get('/accounts/chart'); setAccounts(r.data); } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const filtered = accounts.filter(a => {
    if (typeFilter && a.account_type !== typeFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return a.account_code.toLowerCase().includes(s) || a.account_name.toLowerCase().includes(s);
    }
    return true;
  });

  const grouped = TYPES.map(t => ({ type: t, label: TYPE_LABELS[t], accounts: filtered.filter(a => a.account_type === t) })).filter(g => g.accounts.length > 0);

  const handleSave = async (e) => {
    e.preventDefault(); setFormError('');
    if (!form.account_code || !form.account_name) { setFormError('Code and name required'); return; }
    setFormLoading(true);
    try {
      if (editAcct) { await api.put(`/accounts/chart/${editAcct.id}`, { account_name: form.account_name, account_category: form.account_category }); }
      else { await api.post('/accounts/chart', form); }
      setShowForm(false); setEditAcct(null); fetchAccounts();
    } catch (err) { setFormError(err.response?.data?.detail || 'Failed'); } finally { setFormLoading(false); }
  };

  const handleDelete = async (acct) => {
    if (!window.confirm(`Delete "${acct.account_name}"?`)) return;
    try { await api.delete(`/accounts/chart/${acct.id}`); fetchAccounts(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (a) => { setEditAcct(a); setForm({ account_code: a.account_code, account_name: a.account_name, account_type: a.account_type, account_category: a.account_category }); setShowForm(true); };
  const openAdd = () => { setEditAcct(null); setForm({ account_code: '', account_name: '', account_type: 'expense', account_category: 'other' }); setShowForm(true); };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="chart-of-accounts-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="coa-title">Chart of Accounts</h1>
            <button onClick={openAdd} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white hover:opacity-90 rounded-sm px-4 py-2 font-medium flex items-center gap-2" data-testid="add-account-button"><Plus className="w-5 h-5" /> Add Account</button>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm mb-6 p-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
              <input type="text" placeholder="Search accounts..." className="w-full pl-10 pr-3 py-2 border border-[#e5e7eb] rounded-sm" value={search} onChange={e => setSearch(e.target.value)} data-testid="coa-search" />
            </div>
            <select className="border border-[#e5e7eb] rounded-sm px-3 py-2" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="coa-type-filter">
              <option value="">All Types</option>
              {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
          </div>

          {loading ? <div className="text-center py-12">Loading...</div> : (
            <div className="space-y-6">
              {grouped.map(g => (
                <div key={g.type} className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm">
                  <div className="px-4 py-3 border-b border-[#e5e7eb]" style={{ borderLeftWidth: 4, borderLeftColor: TYPE_COLORS[g.type] }}>
                    <h2 className="font-semibold text-[#1a1a2e]">{g.label}</h2>
                  </div>
                  <table className="w-full text-sm" data-testid={`coa-table-${g.type}`}>
                    <thead className="bg-[#f9fafb] text-[#6b7280]">
                      <tr><th className="py-2 px-4 text-left">Code</th><th className="py-2 px-4 text-left">Name</th><th className="py-2 px-4 text-left">Category</th><th className="py-2 px-4 text-right">Balance (Rs.)</th><th className="py-2 px-4 text-center">Actions</th></tr>
                    </thead>
                    <tbody>
                      {g.accounts.map(a => (
                        <tr key={a.id} className="border-t border-[#e5e7eb] hover:bg-[#f9fafb]">
                          <td className="py-2 px-4 font-mono font-medium">{a.account_code}</td>
                          <td className="py-2 px-4">{a.account_name} {a.is_system_account && <span className="text-xs bg-[#DBEAFE] text-[#1E40AF] px-1.5 py-0.5 rounded-sm ml-1">System</span>}</td>
                          <td className="py-2 px-4 text-[#6b7280] capitalize">{a.account_category}</td>
                          <td className="py-2 px-4 text-right font-medium">{parseFloat(a.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          <td className="py-2 px-4 text-center">
                            <div className="flex justify-center gap-2">
                              {!a.is_system_account && <button onClick={() => openEdit(a)} className="text-[#1E40AF] hover:underline" data-testid={`edit-acct-${a.account_code}`}><Edit2 className="w-4 h-4" /></button>}
                              {!a.is_system_account && <button onClick={() => handleDelete(a)} className="text-[#EF4444] hover:underline" data-testid={`del-acct-${a.account_code}`}><Trash2 className="w-4 h-4" /></button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="account-form-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-[#e5e7eb] flex justify-between items-center">
              <h2 className="text-xl font-semibold">{editAcct ? 'Edit Account' : 'Add Account'}</h2>
              <button onClick={() => setShowForm(false)} className="text-[#6b7280] hover:text-[#1a1a2e]">&times;</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {formError && <div className="p-3 bg-[#FEF2F2] border border-[#FCA5A5] rounded-sm text-[#EF4444] text-sm">{formError}</div>}
              <div>
                <label className="block text-sm font-medium mb-1">Account Code <span className="text-[#EF4444]">*</span></label>
                <input type="text" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={form.account_code} onChange={e => setForm({...form, account_code: e.target.value})} disabled={!!editAcct} data-testid="acct-code-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Account Name <span className="text-[#EF4444]">*</span></label>
                <input type="text" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={form.account_name} onChange={e => setForm({...form, account_name: e.target.value})} data-testid="acct-name-input" />
              </div>
              {!editAcct && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={form.account_type} onChange={e => setForm({...form, account_type: e.target.value})} data-testid="acct-type-select">
                      {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <input type="text" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={form.account_category} onChange={e => setForm({...form, account_category: e.target.value})} data-testid="acct-category-input" />
                  </div>
                </>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-[#e5e7eb] rounded-sm hover:bg-[#f3f4f6] font-medium">Cancel</button>
                <button type="submit" disabled={formLoading} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-sm hover:opacity-90 font-medium disabled:opacity-50" data-testid="save-account-button">{formLoading ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccounts;
